/**
 * O dia do calendário, sem fuso: `AAAA-MM-DD`, e a conta que o `CampoData` e o
 * `CalendarioDeVagas` fazem com ele.
 *
 * ── Por que nada de `toISOString` ────────────────────────────────────────
 *
 * Data de nascimento não tem hora nem fuso: é o dia escrito na certidão. O
 * `new Date().toISOString()` devolve o dia em UTC, e no Brasil, depois das 21h,
 * UTC já é amanhã — o "hoje" do calendário pularia um dia à noite, e "não pode
 * ser depois de hoje" deixaria passar o dia de amanhã. Tudo aqui sai dos
 * componentes LOCAIS (`getFullYear`, `getMonth`, `getDate`), e o `Date` só entra
 * ao meio-dia de um dia, para somar dias e meses sem tropeçar no horário de
 * verão.
 *
 * Sem tela: o que é teclado, foco e posição mora no componente.
 */

/** `AAAA-MM-DD`. */
export type DiaIso = string;

/** O mês é de 0 a 11, como no `Date`. */
export type PartesDoDia = { ano: number; mes: number; dia: number };

export const MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
] as const;

export const SEMANA = [
  'domingo',
  'segunda',
  'terça',
  'quarta',
  'quinta',
  'sexta',
  'sábado',
] as const;

/** A semana começa no domingo. */
export const INICIAIS_DA_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;

/** Sem `min`, o calendário volta 120 anos: a idade de quem ainda nasce num cadastro. */
export const ANOS_PARA_TRAS = 120;
/** Sem `max`, ele vai 20 anos para a frente. */
export const ANOS_PARA_FRENTE = 20;

const doisDigitos = (numero: number) => String(numero).padStart(2, '0');

export function diaDe(ano: number, mes: number, dia: number): DiaIso {
  return `${String(ano).padStart(4, '0')}-${doisDigitos(mes + 1)}-${doisDigitos(dia)}`;
}

/** `AAAA-MM-DD` → ano, mês (0 a 11) e dia, sem conferir se o dia existe. */
export function partes(dia: DiaIso): PartesDoDia {
  const [ano, mes, numero] = dia.split('-').map(Number);
  return { ano: ano!, mes: mes! - 1, dia: numero! };
}

/** Hoje no relógio de quem está na tela, nunca em UTC. */
export function hojeLocal(agora: Date = new Date()): DiaIso {
  return diaDe(agora.getFullYear(), agora.getMonth(), agora.getDate());
}

export function diasNoMes(ano: number, mes: number): number {
  return new Date(ano, mes + 1, 0, 12).getDate();
}

/** O dia existe? 31/02 e 29/02 fora de ano bissexto não. */
export function diaExiste(ano: number, mes: number, dia: number): boolean {
  return (
    Number.isInteger(ano) &&
    Number.isInteger(mes) &&
    Number.isInteger(dia) &&
    ano >= 1 &&
    ano <= 9999 &&
    mes >= 0 &&
    mes <= 11 &&
    dia >= 1 &&
    dia <= diasNoMes(ano, mes)
  );
}

/** `AAAA-MM-DD` de um dia que existe; qualquer outra coisa é `null`. */
export function lerIso(texto: string): DiaIso | null {
  const achado = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto.trim());
  if (!achado) return null;
  const ano = Number(achado[1]);
  const mes = Number(achado[2]) - 1;
  const dia = Number(achado[3]);
  return diaExiste(ano, mes, dia) ? diaDe(ano, mes, dia) : null;
}

/** "terça, 3 de novembro de 2026": o nome do dia para o leitor de tela. */
export function dataPorExtenso(dia: DiaIso): string {
  const p = partes(dia);
  const semana = SEMANA[new Date(p.ano, p.mes, p.dia, 12).getDay()];
  return `${semana}, ${p.dia} de ${MESES[p.mes]} de ${p.ano}`;
}

// ── A máscara dd/mm/aaaa ─────────────────────────────────────────────────

/**
 * Só os dígitos, até oito, com as barras no lugar: "03111990" → "03/11/1990".
 *
 * A barra só entra quando chega o dígito seguinte ("03" fica "03", "031" vira
 * "03/1"). Se ela entrasse logo depois do segundo dígito, o Backspace apagaria
 * a barra, a máscara a poria de volta, e a pessoa não conseguiria apagar.
 */
export function mascarar(texto: string): string {
  // Quem digita "3/" quis dizer "03/": o dia (e o mês) de um dígito ganha o zero.
  const completo = texto.replace(/^(\d)[/.\- ]/, '0$1/').replace(/^(\d{2}\/)(\d)[/.\- ]/, '$10$2/');
  const digitos = completo.replace(/\D/g, '').slice(0, 8);
  if (digitos.length <= 2) return digitos;
  if (digitos.length <= 4) return `${digitos.slice(0, 2)}/${digitos.slice(2)}`;
  return `${digitos.slice(0, 2)}/${digitos.slice(2, 4)}/${digitos.slice(4)}`;
}

/**
 * Onde o cursor fica depois de mascarar: logo depois do mesmo número de
 * dígitos que havia antes dele. Sem isto, apagar um dígito no meio jogaria o
 * cursor para o fim.
 */
export function cursorNoMascarado(mascarado: string, digitosAntes: number): number {
  if (digitosAntes <= 0) return 0;
  let vistos = 0;
  for (let i = 0; i < mascarado.length; i++) {
    if (/\d/.test(mascarado[i]!)) vistos++;
    if (vistos === digitosAntes) return i + 1;
  }
  return mascarado.length;
}

/**
 * Uma data inteira colada, em qualquer das formas de costume: "03/11/1990",
 * "3/11/1990", "03.11.1990", "03-11-1990" ou "1990-11-03" (a que vem de
 * planilha e de sistema). Devolve o texto já mascarado, ou `null` quando não é
 * uma data inteira — aí quem cola fica com a máscara comum.
 */
export function textoColado(texto: string): string | null {
  const limpo = texto.trim();
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(limpo);
  if (iso) return `${doisDigitos(Number(iso[3]))}/${doisDigitos(Number(iso[2]))}/${iso[1]}`;
  const nosso = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(limpo);
  if (nosso) return `${doisDigitos(Number(nosso[1]))}/${doisDigitos(Number(nosso[2]))}/${nosso[3]}`;
  return null;
}

/** `AAAA-MM-DD` → "dd/mm/aaaa"; vazio fica vazio. */
export function textoDoDia(dia: DiaIso | ''): string {
  if (!dia || !lerIso(dia)) return '';
  const p = partes(dia);
  return `${doisDigitos(p.dia)}/${doisDigitos(p.mes + 1)}/${p.ano}`;
}

export type LeituraDaData =
  | { tipo: 'vazio' }
  | { tipo: 'incompleta' }
  | { tipo: 'inexistente' }
  | { tipo: 'antes'; limite: DiaIso }
  | { tipo: 'depois'; limite: DiaIso }
  | { tipo: 'ok'; dia: DiaIso };

/** O que o texto digitado é, contra os limites. `min` e `max` em `AAAA-MM-DD`. */
export function lerTexto(texto: string, min?: string, max?: string): LeituraDaData {
  const limpo = texto.trim();
  if (limpo === '') return { tipo: 'vazio' };
  const achado = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(limpo);
  if (!achado) return { tipo: 'incompleta' };
  const dia = Number(achado[1]);
  const mes = Number(achado[2]) - 1;
  const ano = Number(achado[3]);
  if (!diaExiste(ano, mes, dia)) return { tipo: 'inexistente' };
  const iso = diaDe(ano, mes, dia);
  const piso = min ? lerIso(min) : null;
  const teto = max ? lerIso(max) : null;
  if (piso && iso < piso) return { tipo: 'antes', limite: piso };
  if (teto && iso > teto) return { tipo: 'depois', limite: teto };
  return { tipo: 'ok', dia: iso };
}

/** O valor que o campo manda: o dia, se o texto é data inteira, válida e nos limites; senão `''`. */
export function diaDoTexto(texto: string, min?: string, max?: string): DiaIso | '' {
  const leitura = lerTexto(texto, min, max);
  return leitura.tipo === 'ok' ? leitura.dia : '';
}

/** A frase do erro, ou `null` quando não há o que dizer. */
export function mensagemDaLeitura(
  leitura: LeituraDaData,
  hoje: DiaIso = hojeLocal(),
): string | null {
  switch (leitura.tipo) {
    case 'vazio':
    case 'ok':
      return null;
    case 'incompleta':
      return 'Use dd/mm/aaaa.';
    case 'inexistente':
      return 'Essa data não existe.';
    case 'antes':
      return leitura.limite === hoje
        ? 'A data não pode ser antes de hoje.'
        : `A data não pode ser antes de ${textoDoDia(leitura.limite)}.`;
    case 'depois':
      return leitura.limite === hoje
        ? 'A data não pode ser depois de hoje.'
        : `A data não pode ser depois de ${textoDoDia(leitura.limite)}.`;
  }
}

// ── O calendário ─────────────────────────────────────────────────────────

/** O intervalo que o calendário mostra: `min` e `max`, ou os padrões a partir de hoje. */
export function intervaloDoCalendario(
  hoje: DiaIso,
  min?: string,
  max?: string,
): { piso: DiaIso; teto: DiaIso } {
  const h = partes(hoje);
  const piso = (min ? lerIso(min) : null) ?? diaDe(h.ano - ANOS_PARA_TRAS, 0, 1);
  const teto = (max ? lerIso(max) : null) ?? diaDe(h.ano + ANOS_PARA_FRENTE, 11, 31);
  return { piso, teto };
}

export function limitar(dia: DiaIso, piso: DiaIso, teto: DiaIso): DiaIso {
  if (dia < piso) return piso;
  if (dia > teto) return teto;
  return dia;
}

/** Soma dias (negativo volta). */
export function somarDias(dia: DiaIso, quantos: number): DiaIso {
  const p = partes(dia);
  const d = new Date(p.ano, p.mes, p.dia + quantos, 12);
  return diaDe(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Soma meses mantendo o dia, e quando o mês de chegada é mais curto, fica no
 * último dia dele: 31/01 + 1 mês = 28/02 (ou 29), e não 03/03.
 */
export function somarMeses(dia: DiaIso, quantos: number): DiaIso {
  const p = partes(dia);
  const alvo = new Date(p.ano, p.mes + quantos, 1, 12);
  const ano = alvo.getFullYear();
  const mes = alvo.getMonth();
  return diaDe(ano, mes, Math.min(p.dia, diasNoMes(ano, mes)));
}

/** O mesmo dia em outro mês e ano (os selects do cabeçalho), no último dia se não houver. */
export function noMes(dia: DiaIso, ano: number, mes: number): DiaIso {
  return diaDe(ano, mes, Math.min(partes(dia).dia, diasNoMes(ano, mes)));
}

export function inicioDaSemana(dia: DiaIso): DiaIso {
  const p = partes(dia);
  return somarDias(dia, -new Date(p.ano, p.mes, p.dia, 12).getDay());
}

export function fimDaSemana(dia: DiaIso): DiaIso {
  return somarDias(inicioDaSemana(dia), 6);
}

/**
 * As semanas do mês, de domingo a sábado, para as linhas da grade. A casa de
 * outro mês é `null`: o calendário de data mostra só o mês que diz mostrar.
 */
export function semanasDoMes(ano: number, mes: number): (DiaIso | null)[][] {
  const vazias = new Date(ano, mes, 1, 12).getDay();
  const total = diasNoMes(ano, mes);
  const casas: (DiaIso | null)[] = [
    ...Array.from({ length: vazias }, () => null),
    ...Array.from({ length: total }, (_, i) => diaDe(ano, mes, i + 1)),
  ];
  while (casas.length % 7 !== 0) casas.push(null);
  const semanas: (DiaIso | null)[][] = [];
  for (let i = 0; i < casas.length; i += 7) semanas.push(casas.slice(i, i + 7));
  return semanas;
}

/** Os anos do select, do mais recente ao mais antigo: em nascimento, o mais usado fica em cima. */
export function anosDoIntervalo(piso: DiaIso, teto: DiaIso): number[] {
  const de = partes(piso).ano;
  const ate = partes(teto).ano;
  return Array.from({ length: ate - de + 1 }, (_, i) => ate - i);
}

/**
 * Para onde a tecla leva o foco na grade (o teclado do date picker do APG), ou
 * `null` se ela não anda. Quem chama limita ao intervalo.
 */
export function destinoDaTecla(tecla: string, comShift: boolean, de: DiaIso): DiaIso | null {
  switch (tecla) {
    case 'ArrowLeft':
      return somarDias(de, -1);
    case 'ArrowRight':
      return somarDias(de, 1);
    case 'ArrowUp':
      return somarDias(de, -7);
    case 'ArrowDown':
      return somarDias(de, 7);
    case 'PageUp':
      return somarMeses(de, comShift ? -12 : -1);
    case 'PageDown':
      return somarMeses(de, comShift ? 12 : 1);
    case 'Home':
      return inicioDaSemana(de);
    case 'End':
      return fimDaSemana(de);
    default:
      return null;
  }
}

/** O mês inteiro está fora do intervalo? O select o mostra, mas travado. */
export function mesForaDoIntervalo(ano: number, mes: number, piso: DiaIso, teto: DiaIso): boolean {
  return diaDe(ano, mes, diasNoMes(ano, mes)) < piso || diaDe(ano, mes, 1) > teto;
}
